import React from 'react'

import { DocumentThumbnail } from '@/components/DocumentThumbnail'
import { HueboVoxelLogo } from '@/components/HueboVoxelLogo'
import { readLastHomeTheme, startSizeBounds, writeLastHomeTheme } from '@/components/home/startPrefs'
import { HOME_BACKDROPS, backdropCss, readLastBackdrop, writeLastBackdrop, type HomeBackdrop } from '@/components/home/backdrops'
import {
  DEFAULT_NEW_WORLD_THEME,
  DENSITY_START_SIZES,
  HOME_PREVIEW_THEME_PRESETS,
  type DensityStartSize,
  type HomePreviewTheme,
} from '@/components/home/constants'
import { homePreviewPaletteColors, homePreviewThemeToDocumentMeta } from '@/components/homePreviewTheme'
import type { StartVolumePreviewView } from '@/components/StartVolumePreview'
import { BALL_AND_BLOCK_SAMPLE, createTrueScaleSampleVoxels } from '@/components/homeSampleScene'
import { useDocumentsIndex } from '@/hooks/useDocumentsIndex'
import { createDocumentWithMeta, deleteDocumentRecord, loadDocumentSnapshot, type DocumentMeta } from '@/persistence/local'
import { purgeLegacyLocalStorageThumbnailImages } from '@/persistence/thumbnailStore'
import { PAGES } from '@/pages/catalog'
import { READY_SHELF, SCHOOL_SHELF, shelfOf } from '@/pages/shelves'
import { newPageDocument, pageFamilies, type PageFamily } from '@/play/pageVersions'
import { isEmptyWorld } from '@/pages/seed'
import type { PageCard } from '@/play/pageTypes'
import { buttonSkin } from '@/play/buttonSkin'
import { SizeIconWedge } from '@/play/toolIcons'
import { WorldButton } from '@/play/ui'
import { useRoute } from '@/router'
import { SwatchImage, SwatchImagesProvider } from '@/swatches/SwatchImages'
import { SCENE_SWATCH_TYPE, registerSceneSwatch, type SwatchCell } from '@/swatches/swatchSheet'
import { hexStringToNumber } from '@/utils/color'
import type { Position } from '@/voxelMath'

/**
 * The home screen (`/`, `/documents`).
 *
 * Owner, 2026-10-15: "it should be fast to create from defaults"; then "far too many surfaces";
 * then "we do need a 'Create' or '+' ... tap a color to see it, adjust size, and then create".
 *
 * - The first tile of your worlds is the new world: the world as the editor will open it, drawn by
 *   the renderer at true scale (one grid line per voxel; one voxel ball and one voxel block of fixed
 *   sizes). The editor's size wedge at the top left (drag or tap along it; snaps to the four sizes;
 *   the floor follows live), a + at the top right (the only thing that makes the world), and a row
 *   of theme swatches along the bottom (tiny voxel worlds in each theme; a tap picks one and the
 *   tile shows it; the picked one is lifted over a short ink bar). Theme and size are remembered.
 * - The new world sits by itself at the top. Your worlds follow in one smaller row that runs the full
 *   width and swipes sideways, newest first. Empty worlds are not shown (and an empty world is thrown
 *   away when you leave it). A long press on a world makes it wobble and shows a ×; tap the × to
 *   throw it away, anywhere else to keep it.
 * - All the pages follow below as a plain grid, on the page itself (owner, 2026-10-15: "one row of
 *   previous docs ... and then all of the available pages below", "not inside their own container").
 * - Pages carry their names under their pictures, in a bubbly font (owner, 2026-10-15). Nothing else
 *   has words on screen: other names are only for screen readers and tooltips.
 */

/*
 * Home's 3D parts are their own chunks (each a WebGPU canvas; three stays in the renderer, ADR 0004):
 * the cards, your worlds and the controls draw first, and each picture fills in its place when it
 * has loaded.
 */
const PlayTray = React.lazy(() => import('@/components/home/PlayTray').then((m) => ({ default: m.PlayTray })))
const StartVolumePreview = React.lazy(() => import('@/components/StartVolumePreview').then((m) => ({ default: m.StartVolumePreview })))

const INK = '#1d2b36'
const INK_RGB = '16, 32, 48'
const PAGE_BG = '#eef2f5'
const RADIUS = 28
const GAP = 18
const WORLD_RADIUS = 18
const PAGE_NAME_FONT = "'Fredoka', ui-rounded, 'SF Pro Rounded', system-ui, sans-serif"
const SHADOW = `0 6px 14px rgba(${INK_RGB}, 0.12), 0 1px 3px rgba(${INK_RGB}, 0.08)`
const SHADOW_LIFT = `0 14px 28px rgba(${INK_RGB}, 0.16), 0 2px 5px rgba(${INK_RGB}, 0.08)`
const HEIGHT_RATIO = 0.4
const LAST_SIZE_KEY = 'huebo:home:last-size'
const LONG_PRESS_MS = 550
const DOUBLE_TAP_MS = 350

/**
 * The home's one hover response (owner, 2026-10-15: "slightly grow on hover ... for consistency"):
 * the world picture, the +, world cards and page cards grow a little under a mouse. Touch never
 * hovers, so it has no effect there. Theme swatches lift instead (their own selection language).
 */
const HOVER_SCALE = 1.04
const HOVER_TRANSITION = 'transform 160ms ease-out'
function useHoverGrow() {
  const [hover, setHover] = React.useState(false)
  return {
    hover,
    handlers: {
      onPointerEnter: (e: React.PointerEvent) => { if (e.pointerType === 'mouse') setHover(true) },
      onPointerLeave: () => setHover(false),
    },
  }
}
/** The grow as a transform, composed with a pressed transform when one applies. */
const growTransform = (hover: boolean, pressed?: string) => pressed ?? (hover ? `scale(${HOVER_SCALE})` : 'none')

type SizedStart = DensityStartSize & { bounds: Position }

function readLastSize(): DensityStartSize['id'] {
  try {
    const id = localStorage.getItem(LAST_SIZE_KEY)
    if (DENSITY_START_SIZES.some((s) => s.id === id)) return id as DensityStartSize['id']
  } catch { /* storage unavailable */ }
  return 'medium'
}

function writeLastSize(id: DensityStartSize['id']): void {
  try { localStorage.setItem(LAST_SIZE_KEY, id) } catch { /* storage unavailable */ }
}

function useViewportWidth(): number {
  const [width, setWidth] = React.useState(() => (typeof window === 'undefined' ? 1024 : window.innerWidth))
  React.useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return width
}

function useStartSizes(viewportWidth: number): SizedStart[] {
  return React.useMemo(
    () => DENSITY_START_SIZES.map((size) => ({
      ...size,
      bounds: startSizeBounds(size, { baseAspect: 'screen', heightRatio: HEIGHT_RATIO, minHeight: 1 }),
    })),
    // Bounds follow the screen (density sizes); recompute when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewportWidth],
  )
}

function sameTheme(a: HomePreviewTheme, b: HomePreviewTheme): boolean {
  return a.backgroundColor === b.backgroundColor && a.baseColor === b.baseColor && a.guideColor === b.guideColor
}

/**
 * Which worlds are empty (no voxels once their saved edits are replayed, so a world whose edits were
 * all undone counts as empty, and a page world still exactly its page's seed counts too). Answers are cached per save (id + commit).
 */
const emptinessCache = new Map<string, boolean>()
function useEmptyWorldIds(scenes: readonly DocumentMeta[]): ReadonlySet<string> {
  const [, bump] = React.useReducer((n: number) => n + 1, 0)
  React.useEffect(() => {
    let cancelled = false
    for (const scene of scenes) {
      const key = `${scene.id}:${scene.commitId}`
      if (emptinessCache.has(key)) continue
      loadDocumentSnapshot(scene.id)
        .then((snapshot) => isEmptyWorld(snapshot.base, snapshot.meta))
        .then((empty) => { emptinessCache.set(key, empty) })
        .catch(() => emptinessCache.set(key, false))
        .finally(() => { if (!cancelled) bump() })
    }
    return () => { cancelled = true }
  }, [scenes])
  const empty = new Set<string>()
  for (const scene of scenes) if (emptinessCache.get(`${scene.id}:${scene.commitId}`)) empty.add(scene.id)
  return empty
}

export function Home() {
  const [, navigate] = useRoute()
  const viewportWidth = useViewportWidth()
  const sizes = useStartSizes(viewportWidth)
  const [sizeId, setSizeId] = React.useState<DensityStartSize['id']>(readLastSize)
  const [theme, setTheme] = React.useState<HomePreviewTheme>(() => readLastHomeTheme(DEFAULT_NEW_WORLD_THEME))
  // A tap on a theme swatch picks it (the tile shows it at once, the swatch lifts); the + makes the
  // world (owner, 2026-10-15: one explicit create, not a tap that both chooses and creates). The
  // theme and size picked are remembered.
  const pickTheme = (next: HomePreviewTheme) => {
    setTheme(next)
    writeLastHomeTheme(next)
  }
  const swatchVariant: SwatchVariant = typeof location !== 'undefined' && new URLSearchParams(location.search).get('swatch') === 'flat' ? 'flat' : 'tile'
  const [backdrop, setBackdrop] = React.useState<HomeBackdrop>(readLastBackdrop)
  const nextBackdrop = () => {
    const next = HOME_BACKDROPS[(HOME_BACKDROPS.indexOf(backdrop) + 1) % HOME_BACKDROPS.length]
    setBackdrop(next)
    writeLastBackdrop(next)
  }
  const newCard = newCardVariant()
  // The atlas draws each swatch once, at the biggest size the card shows it.
  const swatchPx = newCard === 'row' ? rowSwatchSize(viewportWidth >= 680) : newCard === 'grid' ? GRID_SWATCH : SWATCH
  const cellPx = Math.round(swatchPx * Math.min(2, typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1))
  const [busy, setBusy] = React.useState(false)
  // Edit mode (owner, 2026-10-15, like iOS): a long press on a world, or on empty space among them,
  // makes every world wobble with a ×. A × throws that world away (several can go); any other tap
  // only leaves edit mode (it opens and makes nothing); Escape leaves too; scrolling still scrolls.
  const [editing, setEditing] = React.useState(false)
  // The long press that started edit mode ends in a click; that one must not leave it again.
  const swallowClick = React.useRef(false)
  const startEditing = React.useCallback(() => {
    swallowClick.current = true
    setEditing(true)
  }, [])
  const gridPress = useLongPress(startEditing)
  const sizeIndex = Math.max(0, sizes.findIndex((s) => s.id === sizeId))
  const size = sizes[sizeIndex] ?? sizes[1]
  // The swatches show the size picked: a bigger world, a bigger floor under the same blocks.
  const swatchItems = React.useMemo(() => HOME_PREVIEW_THEME_PRESETS.map((_, i) => ({ type: SCENE_SWATCH_TYPE, color: themeSceneKey(i, sizeIndex) })), [sizeIndex])
  const allScenes = useDocumentsIndex()
  const emptyIds = useEmptyWorldIds(allScenes)
  const scenes = allScenes.filter((scene) => !emptyIds.has(scene.id))
  React.useEffect(() => {
    if (editing && scenes.length === 0) setEditing(false)
  }, [editing, scenes.length])
  React.useEffect(() => {
    if (!editing) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditing(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [editing])

  // Old builds kept thumbnails in localStorage; clear them out (the previous home did this too).
  React.useEffect(() => { purgeLegacyLocalStorageThumbnailImages() }, [])

  const wide = viewportWidth >= 680
  const gutter = wide ? 28 : 16
  const worldHeight = wide ? 150 : 110

  const pickSize = (index: number) => {
    const next = sizes[index]
    if (!next || next.id === sizeId) return
    setSizeId(next.id)
    writeLastSize(next.id)
  }

  const makeWorld = React.useCallback(async (made: HomePreviewTheme = theme) => {
    if (busy) return
    setBusy(true)
    try {
      const meta = homePreviewThemeToDocumentMeta(made)
      const id = await createDocumentWithMeta({
        bounds: size.bounds,
        ...meta,
        ...(backdrop.wall !== null ? { backgroundColor: backdrop.wall } : {}),
        ...(backdrop.backdrop ? { backdrop: backdrop.backdrop } : {}),
        prefersCameraMode: 'tilt',
      })
      navigate({ page: 'local', id })
    } finally {
      setBusy(false)
    }
  }, [backdrop, busy, navigate, size.bounds, theme])

  return (
    <div style={{ position: 'fixed', inset: 0, background: PAGE_BG, color: INK }}>
      <SwatchImagesProvider items={swatchVariant === 'tile' ? swatchItems : []} cellPx={cellPx}>
      <style>{WOBBLE_CSS}</style>
      <div
        data-home-lab-scroll
        // In edit mode, presses and taps reach only the ×s: anything else is consumed and leaves edit
        // mode on its click. Stopping propagation (not preventing default) keeps native scrolling.
        onPointerDownCapture={(e) => {
          if (!editing) return
          swallowClick.current = false
          if (!isDeleteTarget(e.target)) e.stopPropagation()
        }}
        onClickCapture={(e) => {
          if (!editing || isDeleteTarget(e.target)) return
          e.stopPropagation()
          e.preventDefault()
          if (swallowClick.current) {
            swallowClick.current = false
            return
          }
          setEditing(false)
        }}
        style={{
          position: 'absolute',
          inset: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: `calc(${gutter}px + env(safe-area-inset-top, 0px)) ${gutter}px 40px`,
        }}
      >
        <div style={{ maxWidth: CONTENT_MAX, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* The picture card has the logo above it; the swatch cards carry it in their controls row. */}
          {newCard === 'picture' && !trayTop() && <HomeLogo />}
          {/* The play tray (owner 2026-10-16; the default, ?top=plain for the logo row): the logo in a live drawing on the page itself. */}
          {trayTop() && (
            <div style={{ marginBottom: -18, height: wide ? 200 : 132 }}>
              <React.Suspense fallback={null}><PlayTray theme={theme} height={wide ? 200 : 132} /></React.Suspense>
            </div>
          )}

          <NewWorldTile
            theme={theme}
            onTheme={pickTheme}
            swatchVariant={swatchVariant}
            bounds={size.bounds}
            sizeIndex={sizeIndex}
            sizeCount={sizes.length}
            busy={busy}
            hero={wide}
            onSize={pickSize}
            onMake={(made) => { void makeWorld(made) }}
            backdrop={backdrop}
            onBackdrop={nextBackdrop}
          />

          {scenes.length > 0 && (
          // The worlds made here, in one container (owner, 2026-10-15: "put docs in a 'My Stuff'
          // container").
          <section aria-label="My Stuff" style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '14px 0 2px', borderRadius: RADIUS, background: '#ffffff', border: `3.5px solid ${INK}` }}>
          <h2 style={{ ...SHELF_HEADING, paddingInline: MY_STUFF_PAD }}>My Stuff</h2>
          <div
            aria-label="My worlds"
            // A long press on empty space among the worlds enters edit mode too.
            onPointerDown={(e) => { if (e.target === e.currentTarget) gridPress.start(e) }}
            onPointerUp={gridPress.cancel}
            onPointerLeave={gridPress.cancel}
            onPointerCancel={gridPress.cancel}
            // Two rows that scroll sideways inside the container (owner, 2026-10-15), filled a column
            // at a time; the padding leaves room for the hover grow and the ×.
            style={{
              display: 'grid',
              gridTemplateRows: `repeat(${Math.min(2, scenes.length)}, ${worldHeight}px)`,
              gridAutoFlow: 'column',
              gridAutoColumns: Math.round((worldHeight * 4) / 3),
              justifyContent: 'start',
              gap: GAP,
              padding: `16px ${MY_STUFF_PAD}px`,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollSnapType: 'x proximity',
              scrollPaddingInline: MY_STUFF_PAD,
              overscrollBehaviorX: 'contain',
              scrollbarWidth: 'none',
            }}
          >
            {scenes.map((scene, index) => (
              <WorldTile
                key={scene.id}
                scene={scene}
                index={index}
                editing={editing}
                onOpen={() => navigate({ page: 'local', id: scene.id })}
                onLongPress={startEditing}
                onDelete={() => deleteDocumentRecord(scene.id)}
              />
            ))}
          </div>
          </section>
          )}

          <PageShelf />
        </div>
      </div>
      </SwatchImagesProvider>
    </div>
  )
}

/** The home column's widest. */
const CONTENT_MAX = 1280

// The play tray is the default top (owner, 2026-10-16: "make this default"); ?top=plain: the logo row.
const trayTop = () => typeof location === 'undefined' || new URLSearchParams(location.search).get('top') !== 'plain'
/** The My Stuff container's inner side padding. */
const MY_STUFF_PAD = 18

// ---- The new world --------------------------------------------------------------------------------

/**
 * The new world: the world as the editor will open it, the size wedge at the top left, the + at the
 * top right (the only thing here that makes the world), and the theme swatches along the bottom
 * (a tap picks one; the tile shows it). The tile itself does nothing when tapped.
 */
function NewWorldTile({ theme: shownTheme, onTheme, swatchVariant, bounds, sizeIndex, sizeCount, busy, hero, onSize, onMake, backdrop, onBackdrop }: {
  theme: HomePreviewTheme
  onTheme: (theme: HomePreviewTheme) => void
  swatchVariant: SwatchVariant
  bounds: Position
  sizeIndex: number
  sizeCount: number
  busy: boolean
  hero: boolean
  onSize: (index: number) => void
  /** Make the world, in the theme given (or the one picked). */
  onMake: (theme?: HomePreviewTheme) => void
  backdrop: HomeBackdrop
  onBackdrop: () => void
}) {
  // Default: no world picture, one row of big theme swatches (owner, 2026-10-15: trying it; we may
  // switch back). ?newcard=picture: the world picture over the small swatch row. ?newcard=grid: the
  // swatches in two rows.
  const variant = newCardVariant()
  if (variant !== 'picture') {
    return (
      <NewWorldGridTile
        rows={variant === 'row' ? 1 : 2}
        swatchSize={variant === 'row' ? rowSwatchSize(hero) : GRID_SWATCH}
        backdrop={backdrop}
        onBackdrop={onBackdrop}
        swatchVariant={swatchVariant}
        sizeIndex={sizeIndex}
        sizeCount={sizeCount}
        busy={busy}
        hero={hero}
        onSize={onSize}
        onMake={onMake}
      />
    )
  }
  return <NewWorldPictureTile theme={shownTheme} onTheme={onTheme} swatchVariant={swatchVariant} bounds={bounds} sizeIndex={sizeIndex} sizeCount={sizeCount} busy={busy} hero={hero} onSize={onSize} onMake={() => onMake()} />
}

function NewWorldPictureTile({ theme: shownTheme, onTheme, swatchVariant, bounds, sizeIndex, sizeCount, busy, hero, onSize, onMake }: {
  theme: HomePreviewTheme
  onTheme: (theme: HomePreviewTheme) => void
  swatchVariant: SwatchVariant
  bounds: Position
  sizeIndex: number
  sizeCount: number
  busy: boolean
  hero: boolean
  onSize: (index: number) => void
  onMake: () => void
}) {
  const picture = useHoverGrow()
  const plus = useHoverGrow()
  const wedgeSize = hero ? 96 : 80
  const floorsSize = hero ? 136 : 112
  // ?size=wedge: the editor's size wedge instead of the growing floor (for comparison).
  const sizeGlyph: SizeGlyph = typeof location !== 'undefined' && new URLSearchParams(location.search).get('size') === 'wedge' ? 'wedge' : 'floors'
  const glyphSize = sizeGlyph === 'floors' ? floorsSize : wedgeSize
  // ?new=doc: a fresh page with a sparkle instead of the + (owner, 2026-10-15: comparing the two).
  const newIcon = typeof location !== 'undefined' && new URLSearchParams(location.search).get('new') === 'doc' ? 'doc' : 'plus'
  const makeSize = hero ? 64 : 56
  const inset = hero ? 14 : 10
  // The size wedge (top left) and the + (top right) share the top band, the swatches the bottom one;
  // the picture gets equal room above and below so it sits in the middle (owner, 2026-10-15).
  const band = Math.max(inset + makeSize + 6, ROW_HEIGHT)
  const first = `#${homePreviewPaletteColors(shownTheme)[0].toString(16).padStart(6, '0')}`
  // The + in the theme's first colour, unless that is too pale to read on the white button.
  const accent = inkOrWhite(first) === INK && luminance(first) > 0.72 ? INK : first
  return (
    <div
      aria-label="New world"
      style={{
        position: 'relative',
        // Full width (owner, 2026-10-15); the height stops where a 640-wide card's did, and the
        // world and the swatch row stay centred.
        width: '100%',
        aspectRatio: hero ? '4 / 3' : '1 / 1',
        maxHeight: hero ? 340 : 300,
        borderRadius: RADIUS + 6,
        overflow: 'hidden',
        background: shownTheme.backgroundColor,
        boxShadow: SHADOW_LIFT,
        transition: 'background-color 160ms',
        opacity: busy ? 0.85 : 1,
      }}
    >
      {/* The floor sits between the wedge and the swatch row, never under either. The picture is also
          a way in: it grows a little under the pointer and a tap makes the world (owner, 2026-10-15). */}
      <button
        type="button"
        aria-label="Make this world"
        title="Make this world"
        onClick={onMake}
        {...picture.handlers}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: band,
          bottom: band,
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            transform: growTransform(picture.hover),
            transition: HOVER_TRANSITION,
          }}
        >
          <TrueScaleWorld bounds={bounds} theme={shownTheme} />
        </span>
      </button>
      {/* The size wedge at the top left, on the create button's centre line (they share a row). */}
      <div style={{ position: 'absolute', top: inset + (makeSize - glyphSize * 0.5) / 2, left: inset }}>
        <SizeWedge
          stops={sizeCount}
          value={sizeIndex}
          onChange={onSize}
          size={glyphSize}
          color={inkOrWhite(shownTheme.backgroundColor)}
          glyph={sizeGlyph}
        />
      </div>
      <div {...plus.handlers} style={{ position: 'absolute', top: inset, right: inset, transform: growTransform(plus.hover), transition: HOVER_TRANSITION }}>
        <WorldButton label="Make this world" size={makeSize} ring={INK} onPress={onMake}>
          {newIcon === 'doc'
            ? <NewDocGlyph color={first} size={Math.round(makeSize * 0.66)} />
            : <PlusGlyph color={accent} size={Math.round(makeSize * 0.56)} />}
        </WorldButton>
      </div>
      <ThemeRow current={shownTheme} variant={swatchVariant} onPick={onTheme} onMake={onMake} />
    </div>
  )
}

/** Big theme swatches on the grid card. */
const GRID_SWATCH = 76
/** Theme swatches on the one-row card: about 2.5x the picture card's (2x on a phone). */
const rowSwatchSize = (hero: boolean) => (hero ? 132 : 104)

type NewCardVariant = 'row' | 'grid' | 'picture'
function newCardVariant(): NewCardVariant {
  const param = typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('newcard')
  return param === 'grid' || param === 'picture' ? param : 'row'
}

/**
 * The new world without its picture (the default one row, or `?newcard=grid`): the size and the
 * background toggle along the top, then the themes as big swatches in one or two rows, centred,
 * that scroll sideways when they do not fit. A tap on a theme makes the world (owner, 2026-10-15:
 * "no need to 'select' a color any more - just tap one and create"). The card wears the background.
 */
function NewWorldGridTile({ rows, swatchSize, backdrop, onBackdrop, swatchVariant, sizeIndex, sizeCount, busy, hero, onSize, onMake }: {
  rows: number
  swatchSize: number
  backdrop: HomeBackdrop
  onBackdrop: () => void
  swatchVariant: SwatchVariant
  sizeIndex: number
  sizeCount: number
  busy: boolean
  hero: boolean
  onSize: (index: number) => void
  onMake: (theme: HomePreviewTheme) => void
}) {
  const inset = hero ? 8 : 6
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    {/* Above the card: the logo at the left, and at the right the background, then the sizes (owner,
        2026-10-16: first down the card's left edge, then "or put them above the card?", then "put
        the logo in their row"), so the card is only the worlds. */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 12 }}>
      {!trayTop() && <HomeLogo />}
      <span style={{ flex: 1 }} />
      <BackdropToggle backdrop={backdrop} onPress={onBackdrop} ink={INK} />
      <SizeChips count={sizeCount} value={sizeIndex} onChange={onSize} ink={INK} />
    </div>
    <div
      aria-label="New world"
      style={{
        position: 'relative',
        width: '100%',
        borderRadius: RADIUS + 6,
        overflow: 'hidden',
        background: backdropCss(backdrop.backdrop) ?? PLAIN_CARD,
        boxShadow: SHADOW_LIFT,
        opacity: busy ? 0.85 : 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        aria-label="Theme"
        style={{
          display: 'grid',
          gridTemplateRows: `repeat(${rows}, ${swatchSize + 10}px)`,
          gridAutoFlow: 'column',
          gridAutoColumns: swatchSize,
          justifyContent: 'safe center',
          columnGap: 16,
          rowGap: 12,
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          padding: `${inset}px 18px ${inset + 4}px`,
          scrollSnapType: 'x proximity',
          scrollPaddingInline: 18,
          overscrollBehaviorX: 'contain',
          scrollbarWidth: 'none',
        }}
      >
        {HOME_PREVIEW_THEME_PRESETS.map((preset, index) => (
          <ThemeSwatch
            key={preset.id}
            label={`New ${preset.label} world`}
            theme={preset.theme}
            index={index}
            variant={swatchVariant}
            selected={false}
            onPress={() => onMake(preset.theme)}
            size={swatchSize}
          />
        ))}
      </div>
    </div>
    </div>
  )
}

function HomeLogo() {
  return (
    <div style={{ flex: 'none', width: 56, height: 56 }} aria-hidden>
      <HueboVoxelLogo showTile={false} className="h-full w-full" />
    </div>
  )
}

/** The new-world card without a background picked: a quiet white. */
const PLAIN_CARD = '#fbfaf7'

/** The background toggle: a little window of the background; a tap steps to the next one. */
function BackdropToggle({ backdrop, onPress, ink }: { backdrop: HomeBackdrop; onPress: () => void; ink: string }) {
  const grow = useHoverGrow()
  return (
    <button
      type="button"
      aria-label={`Background: ${backdrop.label}`}
      title={`Background: ${backdrop.label}`}
      onClick={onPress}
      {...grow.handlers}
      style={{
        width: 34,
        height: 34,
        padding: 0,
        borderRadius: 10,
        border: `2px solid ${ink}`,
        background: backdropCss(backdrop.backdrop) ?? PLAIN_SWATCH,
        cursor: 'pointer',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        transform: growTransform(grow.hover),
        transition: HOVER_TRANSITION,
      }}
    />
  )
}

/**
 * The sizes side by side (owner, 2026-10-15: the floors glyph was not legible; then one rounded
 * border, then "more subtle", then "do away with the border"): each a floor seen
 * from above, bigger for a bigger world, its cells all one size (a bigger world has more of them).
 * Drawn in `ink` (ink or white for the background); the picked one sits on it, lines reversed.
 */
function SizeChips({ count, value, onChange, ink, vertical = false }: { count: number; value: number; onChange: (index: number) => void; ink: string; vertical?: boolean }) {
  return (
    <div
      role="radiogroup"
      aria-label="Size"
      style={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: 0,
        opacity: 0.85,
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <SizeChip key={i} index={i} cells={i + 2} selected={i === value} onPress={() => onChange(i)} ink={ink} />
      ))}
    </div>
  )
}

function SizeChip({ index, cells, selected, onPress, ink }: { index: number; cells: number; selected: boolean; onPress: () => void; ink: string }) {
  const grow = useHoverGrow()
  const back = ink === INK ? '#ffffff' : INK
  const face = selected ? ink : 'transparent'
  const line = selected ? back : ink
  const step = 4.6
  const side = step * cells
  const box = 24
  const name = DENSITY_START_SIZES[index]?.label ?? `Size ${index + 1}`
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={name}
      title={name}
      onClick={onPress}
      {...grow.handlers}
      style={{
        width: 34,
        height: 30,
        padding: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 10,
        border: 'none',
        background: face,
        cursor: 'pointer',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        transform: growTransform(grow.hover),
        transition: `${HOVER_TRANSITION}, background-color 120ms ease-out`,
      }}
    >
      <svg width={box} height={box} viewBox={`${(side - box) / 2} ${(side - box) / 2} ${box} ${box}`} aria-hidden style={{ display: 'block' }}>
        <rect x={0} y={0} width={side} height={side} rx={2} fill="none" stroke={line} strokeWidth={2} />
        <g stroke={line} strokeWidth={1.2} strokeOpacity={0.75}>
          {Array.from({ length: cells - 1 }, (_, k) => (
            <React.Fragment key={k}>
              <line x1={(k + 1) * step} y1={0} x2={(k + 1) * step} y2={side} />
              <line x1={0} y1={(k + 1) * step} x2={side} y2={(k + 1) * step} />
            </React.Fragment>
          ))}
        </g>
      </svg>
    </button>
  )
}

/** Plain on the toggle: the themes' own colours, a quarter each. */
const PLAIN_SWATCH = 'conic-gradient(#8fd17a 0 25%, #ffd36b 0 50%, #7cc4f2 0 75%, #f59ac0 0)'

function luminance(hex: string): number {
  const n = parseInt(hex.replace('#', ''), 16)
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255
}

function inkOrWhite(background: string): string {
  return luminance(background) > 0.55 ? INK : '#ffffff'
}

/**
 * The size control is the editor's size wedge and nothing else: filled up to the current size, the
 * rest faint. Drag along it (or tap a spot) and it snaps to the nearest of the sizes. The hit area
 * is generous; only the wedge is drawn.
 */
function SizeWedge({ stops, value, onChange, size, color, glyph }: {
  stops: number
  value: number
  onChange: (stop: number) => void
  size: number
  color: string
  glyph: SizeGlyph
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const dragging = React.useRef<number | null>(null)
  const n = Math.max(2, stops)
  // The wedge spans x 3..38 of the icon's 40-unit box; its filled part ends at 3 + (0.12 + 0.88 t) * 35.
  const stopAt = (clientX: number) => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return value
    if (glyph === 'floors') {
      // Nearest plate centre.
      const f = (clientX - r.left) / r.width
      const centres = floorPlates(n).map((p) => p.cx / FLOORS_VIEW_W)
      let best = 0
      for (let i = 1; i < centres.length; i++) if (Math.abs(centres[i] - f) < Math.abs(centres[best] - f)) best = i
      return best
    }
    const x = ((clientX - r.left) / r.width) * 40
    const t = ((x - 3) / 35 - 0.12) / 0.88
    return Math.round(Math.max(0, Math.min(1, t)) * (n - 1))
  }
  return (
    <div
      ref={ref}
      role="slider"
      aria-label="Size"
      aria-valuemin={0}
      aria-valuemax={n - 1}
      aria-valuenow={value}
      tabIndex={0}
      onPointerDown={(e) => {
        dragging.current = e.pointerId
        try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
        onChange(stopAt(e.clientX))
      }}
      onPointerMove={(e) => { if (dragging.current === e.pointerId) onChange(stopAt(e.clientX)) }}
      onPointerUp={() => { dragging.current = null }}
      onPointerCancel={() => { dragging.current = null }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') onChange(Math.max(0, value - 1))
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') onChange(Math.min(n - 1, value + 1))
      }}
      style={{
        width: size,
        height: size * 0.5,
        display: 'grid',
        placeItems: 'center',
        cursor: 'ew-resize',
        touchAction: 'none',
        outline: 'none',
        filter: color === '#ffffff' ? `drop-shadow(0 2px 4px rgba(${INK_RGB}, 0.35))` : 'none',
      }}
    >
      {glyph === 'floors' ? (
        <FloorsGlyph width={size} stops={n} value={value} color={color} />
      ) : (
        // The icon is square with the wedge across its middle; the box is the wedge's band only.
        <div style={{ margin: `${-size / 4}px 0`, lineHeight: 0 }}>
          <SizeIconWedge size={size} color={color} level={value / (n - 1)} />
        </div>
      )}
    </div>
  )
}

type SizeGlyph = 'floors' | 'wedge'

const FLOORS_VIEW_W = 100
const FLOORS_VIEW_H = 34
/** Plate sizes, smallest to largest, as fractions of the largest. */
const FLOOR_PLATE_SCALES = [0.42, 0.6, 0.8, 1]
const FLOOR_PLATE_MAX = 30
const FLOOR_PLATE_GAP = 3

/** The plates' layout in the glyph's view box: centre x, bottom and top widths, depth. */
function floorPlates(n: number) {
  const scales = Array.from({ length: n }, (_, i) => FLOOR_PLATE_SCALES[Math.round((i / Math.max(1, n - 1)) * (FLOOR_PLATE_SCALES.length - 1))])
  const widths = scales.map((k) => FLOOR_PLATE_MAX * k)
  const total = widths.reduce((a, b) => a + b, 0) + FLOOR_PLATE_GAP * (n - 1)
  let x = (FLOORS_VIEW_W - total) / 2
  const bottom = FLOORS_VIEW_H / 2 + FLOOR_PLATE_MAX * 0.275
  return widths.map((w) => {
    const plate = { cx: x + w / 2, bottomW: w, topW: w * 0.72, depth: w * 0.55, bottom }
    x += w + FLOOR_PLATE_GAP
    return plate
  })
}

/**
 * The size glyph as a growing floor: one small tilted floor plate per size, smallest on the left,
 * drawn like the tile's own floor (a trapezoid seen from the tilted camera, with a hint of grid). The
 * current size's plate is solid; the others are faint outlines. "How big is the world", not volume.
 */
function FloorsGlyph({ width, stops, value, color }: { width: number; stops: number; value: number; color: string }) {
  const plates = floorPlates(stops)
  const line = color === '#ffffff' ? INK : '#ffffff'
  return (
    <svg width={width} height={width * (FLOORS_VIEW_H / FLOORS_VIEW_W)} viewBox={`0 0 ${FLOORS_VIEW_W} ${FLOORS_VIEW_H}`} aria-hidden style={{ display: 'block', overflow: 'visible' }}>
      {plates.map((p, i) => {
        const top = p.bottom - p.depth
        const pts = `${p.cx - p.topW / 2},${top} ${p.cx + p.topW / 2},${top} ${p.cx + p.bottomW / 2},${p.bottom} ${p.cx - p.bottomW / 2},${p.bottom}`
        const on = i === value
        return (
          <g key={i}>
            <polygon points={pts} fill={color} fillOpacity={on ? 1 : 0.14} stroke={color} strokeOpacity={on ? 1 : 0.55} strokeWidth={1.6} strokeLinejoin="round" />
            {on && (
              <g stroke={line} strokeOpacity={0.35} strokeWidth={1}>
                <line x1={p.cx} y1={top} x2={p.cx} y2={p.bottom} />
                <line x1={p.cx - (p.topW + p.bottomW) / 4} y1={top + p.depth / 2} x2={p.cx + (p.topW + p.bottomW) / 4} y2={top + p.depth / 2} />
              </g>
            )}
          </g>
        )
      })}
    </svg>
  )
}

/**
 * The themes as swatches floating along the tile's bottom. Swipe along; tap one to pick it. The
 * picked one is lifted over a short ink bar, like the palette's selection.
 */
function ThemeRow({ current, variant, onPick, onMake }: {
  current: HomePreviewTheme
  variant: SwatchVariant
  onPick: (theme: HomePreviewTheme) => void
  onMake: () => void
}) {
  // A second tap on the same swatch soon after the first (a double tap or double click) makes the
  // world in that theme; the first tap has already picked it, so it shows at once.
  const lastTap = React.useRef<{ id: string; at: number } | null>(null)
  const tap = (id: string, theme: HomePreviewTheme) => {
    const now = performance.now()
    const previous = lastTap.current
    lastTap.current = { id, at: now }
    onPick(theme)
    if (previous && previous.id === id && now - previous.at < DOUBLE_TAP_MS) {
      lastTap.current = null
      onMake()
    }
  }
  return (
    <div
      aria-label="Theme"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'safe center',
        gap: 14,
        overflowX: 'auto',
        padding: '14px 18px 18px',
        scrollSnapType: 'x proximity',
        scrollPaddingInline: 18,
        overscrollBehaviorX: 'contain',
        scrollbarWidth: 'none',
      }}
    >
      {HOME_PREVIEW_THEME_PRESETS.map((preset, index) => (
        <ThemeSwatch
          key={preset.id}
          label={preset.label}
          theme={preset.theme}
          index={index}
          variant={variant}
          selected={sameTheme(preset.theme, current)}
          onPress={() => tap(preset.id, preset.theme)}
        />
      ))}
    </div>
  )
}

const SWATCH = 52
/** Room the swatch row takes at the tile's bottom (swatch, lift, selection bar, padding). */
const ROW_HEIGHT = 96

type SwatchVariant = 'tile' | 'flat'

/** Scene-swatch keys for the themes (`registerSceneSwatch`); any numbers clear of colour values work. */
const themeSceneKey = (index: number, sizeIndex: number) => 0x7e0000 + sizeIndex * 0x100 + index
/** The swatch floor's side per start size (small to huge): the blocks stay, the floor grows. */
const SWATCH_FLOOR = [3, 4, 5, 6]

/**
 * Variant "tile": the theme as a tiny world, drawn as voxels by the shared swatch atlas: a 4 x 4
 * floor in its floor colour (every other cell a step toward its grid colour, a hint of the grid) with
 * a few blocks of its palette colours on it. Every theme has the same layout, so one measured fit.
 */
function themeSceneCells(theme: HomePreviewTheme, n: number): SwatchCell[] {
  const base = hexStringToNumber(theme.baseColor)
  const line = mixHex(base, hexStringToNumber(theme.guideColor), 0.5)
  const colors = homePreviewPaletteColors(theme)
  const at = (i: number) => colors[i % colors.length]
  const cells: SwatchCell[] = []
  for (let x = 0; x < n; x++) for (let z = 0; z < n; z++) cells.push([x, 0, z, { type: 'solid', color: (x + z) % 2 ? line : base }])
  // The same three blocks at the same places relative to the floor (a 4 x 4 floor is the old layout).
  const at4 = (i: number) => Math.min(n - 1, Math.round((i * (n - 1)) / 3))
  cells.push([at4(0), 1, at4(0), { type: 'solid', color: at(0) }], [at4(0), 2, at4(0), { type: 'solid', color: at(0) }])
  cells.push([at4(2), 1, at4(2), { type: 'solid', color: at(1) }])
  cells.push([at4(3), 1, at4(0), { type: 'solid', color: at(2) }])
  return cells
}
HOME_PREVIEW_THEME_PRESETS.forEach((preset, i) => SWATCH_FLOOR.forEach((n, sizeIndex) => registerSceneSwatch(themeSceneKey(i, sizeIndex), themeSceneCells(preset.theme, n), `floor-${n}`)))

function mixHex(a: number, b: number, t: number): number {
  const ch = (shift: number) => Math.round(((a >> shift) & 255) * (1 - t) + ((b >> shift) & 255) * t)
  return (ch(16) << 16) | (ch(8) << 8) | ch(0)
}

/** Variant "flat": the floor colour with a finer grid, a few cells filled with its palette colours. */
function FlatThemeSwatch({ theme }: { theme: HomePreviewTheme }) {
  const colors = homePreviewPaletteColors(theme)
  const at = (i: number) => `#${colors[i % colors.length].toString(16).padStart(6, '0')}`
  const n = 6
  const c = SWATCH / n
  const filled: [number, number, number][] = [[1, 3, 0], [1, 4, 0], [2, 4, 0], [3, 2, 1], [4, 4, 2], [4, 3, 2]]
  return (
    <svg width={SWATCH} height={SWATCH} viewBox={`0 0 ${SWATCH} ${SWATCH}`} aria-hidden style={{ display: 'block' }}>
      {filled.map(([x, y, k]) => <rect key={`${x}-${y}`} x={x * c} y={y * c} width={c} height={c} fill={at(k)} />)}
      <g stroke={theme.guideColor} strokeWidth={1}>
        {Array.from({ length: n - 1 }, (_, i) => (
          <React.Fragment key={i}>
            <line x1={(i + 1) * c} y1={0} x2={(i + 1) * c} y2={SWATCH} />
            <line x1={0} y1={(i + 1) * c} x2={SWATCH} y2={(i + 1) * c} />
          </React.Fragment>
        ))}
      </g>
    </svg>
  )
}

function ThemeSwatch({ label, theme, index, variant, selected, onPress, size = SWATCH }: {
  label: string
  theme: HomePreviewTheme
  index: number
  variant: SwatchVariant
  selected: boolean
  onPress: () => void
  /** Swatch size in px (default SWATCH; the grid card's are bigger). */
  size?: number
}) {
  const [down, setDown] = React.useState(false)
  const [hover, setHover] = React.useState(false)
  const lift = down ? 1 : selected ? -5 : hover ? -2 : 0
  const tile = variant === 'tile'
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={selected}
      onClick={onPress}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHover(true) }}
      onPointerDown={() => setDown(true)}
      onPointerUp={() => setDown(false)}
      onPointerLeave={() => { setDown(false); setHover(false) }}
      onPointerCancel={() => setDown(false)}
      style={{
        position: 'relative',
        flex: '0 0 auto',
        width: size,
        height: size + 10,
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        scrollSnapAlign: 'start',
        touchAction: 'pan-x',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 5,
          width: size,
          height: size,
          borderRadius: tile ? 0 : 16,
          overflow: tile ? 'visible' : 'hidden',
          background: tile ? 'transparent' : theme.baseColor,
          transform: `translateY(${lift}px)`,
          transition: 'transform 120ms ease-out, box-shadow 120ms ease-out, filter 120ms ease-out',
          // A rendered tile floats by its own silhouette (drop-shadow); a flat swatch by its box.
          ...(tile
            ? { filter: selected ? `drop-shadow(0 8px 7px rgba(${INK_RGB}, 0.4))` : `drop-shadow(0 3px 3px rgba(${INK_RGB}, 0.3))` }
            : {
                boxShadow: selected
                  ? `0 10px 16px rgba(${INK_RGB}, 0.32), 0 2px 4px rgba(${INK_RGB}, 0.2)`
                  : `0 4px 8px rgba(${INK_RGB}, 0.26), 0 1px 2px rgba(${INK_RGB}, 0.16)`,
              }),
        }}
      >
        {tile ? <SwatchImage index={index} size={size} /> : <FlatThemeSwatch theme={theme} />}
      </span>
      {selected && (
        <span aria-hidden style={{ position: 'absolute', left: '50%', bottom: -4, width: 18, height: 4, marginLeft: -9, borderRadius: 2, background: inkOrWhite(theme.backgroundColor) === INK ? INK : 'rgba(255,255,255,0.85)' }} />
      )}
    </button>
  )
}

/**
 * The world drawn by the renderer, from a little lower than the editor's tilt: the document's own
 * floor and grid (cell = voxel, the editor's line spacing), one voxel ball and one voxel block of
 * fixed sizes in voxels, so their steps show how big a voxel is. One canvas.
 */
function TrueScaleWorld({ bounds, theme }: { bounds: Position; theme: HomePreviewTheme }) {
  const colors = React.useMemo(() => homePreviewPaletteColors(theme), [theme])
  const voxels = React.useMemo(() => createTrueScaleSampleVoxels(bounds, colors, BALL_AND_BLOCK_SAMPLE), [bounds, colors])
  const view = React.useMemo<StartVolumePreviewView>(() => ({
    orthographic: false,
    // A lower camera than the editor's tilt (about 35° above the floor, not 49°) so the floor reads
    // as a shallow band and the card can be short (owner, 2026-10-15).
    cameraPosition: [0, 3.9, 5.55],
    cameraFov: 30,
    lookAt: [0, 0.1, 0],
    maxSpan: 4.4,
    scenePosition: [0, 0, 0],
    sceneRotationY: 0,
    showBase: true,
    showGrid: true,
    showFrame: false,
  }), [bounds.x, bounds.z])
  return (
    <React.Suspense fallback={null}>
      <StartVolumePreview bounds={bounds} accent={false} theme={theme} voxels={voxels} view={view} />
    </React.Suspense>
  )
}

// ---- My worlds -------------------------------------------------------------------------------------

const WOBBLE_CSS = `
@keyframes huebo-home-wobble { 0% { transform: rotate(-0.9deg) } 50% { transform: rotate(0.9deg) } 100% { transform: rotate(-0.9deg) } }
.huebo-home-wobble { animation: huebo-home-wobble 240ms ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .huebo-home-wobble { animation: none; } }
`

const isDeleteTarget = (target: EventTarget | null) => Boolean((target as Element | null)?.closest?.('[data-world-delete]'))

/** A long press: `start` on pointer down, `cancel` on up/leave; `onLongPress` after LONG_PRESS_MS. */
function useLongPress(onLongPress: () => void) {
  const timer = React.useRef<number | null>(null)
  const cancel = React.useCallback(() => {
    if (timer.current != null) window.clearTimeout(timer.current)
    timer.current = null
  }, [])
  React.useEffect(() => cancel, [cancel])
  const start = React.useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    cancel()
    timer.current = window.setTimeout(() => { timer.current = null; onLongPress() }, LONG_PRESS_MS)
  }, [cancel, onLongPress])
  return { start, cancel }
}

/** A world: tap opens it; a long press enters edit mode (every world wobbles with a ×). */
function WorldTile({ scene, index, editing, onOpen, onLongPress, onDelete }: {
  scene: DocumentMeta
  index: number
  editing: boolean
  onOpen: () => void
  onLongPress: () => void
  onDelete: () => void
}) {
  const [down, setDown] = React.useState(false)
  const grow = useHoverGrow()
  const timer = React.useRef<number | null>(null)
  const longFired = React.useRef(false)
  const clear = () => {
    if (timer.current != null) window.clearTimeout(timer.current)
    timer.current = null
  }
  React.useEffect(() => clear, [])
  return (
    <div
      className={editing ? 'huebo-home-wobble' : undefined}
      style={{
        position: 'relative',
        flex: 'none',
        height: '100%',
        aspectRatio: '4 / 3',
        scrollSnapAlign: 'start',
        // Out of step with each other, like a shelf of things nudged at once.
        animationDelay: `${-(index * 67) % 240}ms`,
      }}
    >
      <button
        type="button"
        aria-label={scene.name || 'World'}
        title={scene.name || 'World'}
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={(e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return
          setDown(true)
          longFired.current = false
          clear()
          timer.current = window.setTimeout(() => {
            longFired.current = true
            setDown(false)
            onLongPress()
          }, LONG_PRESS_MS)
        }}
        onPointerUp={() => { setDown(false); clear() }}
        onPointerEnter={grow.handlers.onPointerEnter}
        onPointerLeave={() => { setDown(false); clear(); grow.handlers.onPointerLeave() }}
        onPointerCancel={() => { setDown(false); clear() }}
        onClick={() => {
          if (longFired.current || editing) return
          onOpen()
        }}
        style={{
          position: 'absolute',
          inset: 0,
          border: 'none',
          padding: 0,
          borderRadius: WORLD_RADIUS,
          overflow: 'hidden',
          cursor: 'pointer',
          background: '#ffffff',
          boxShadow: down ? SHADOW : SHADOW_LIFT,
          transform: growTransform(grow.hover, down ? 'translateY(2px) scale(0.985)' : undefined),
          transition: `${HOVER_TRANSITION}, box-shadow 120ms ease-out`,
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          WebkitTouchCallout: 'none',
          userSelect: 'none',
        }}
      >
        <DocumentThumbnail id={scene.id} width={320} height={240} meta={scene} alt="" showCommitBadge={false} style={{ width: '100%', height: '100%' }} className="h-full w-full object-cover" />
      </button>
      {editing && (
        <button
          type="button"
          data-world-delete
          aria-label="Throw away"
          title="Throw away"
          onClick={onDelete}
          style={{ position: 'absolute', left: -10, top: -10, width: 40, height: 40, borderRadius: 20, border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center', ...buttonSkin({ state: 'rest', ledge: 3 }) }}
        >
          <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden>
            <path d="M4 4 L12 12 M12 4 L4 12" stroke={INK} strokeWidth={2.8} strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  )
}

// ---- Pages: a plain grid below the worlds --------------------------------------------------

function PageShelf() {
  const [, navigate] = useRoute()
  const [busy, setBusy] = React.useState<string | null>(null)
  // One card per page family (src/play/pageVersions.ts): the newest open version opens. The shelves
  // (src/pages/shelves.ts): the ready pages big and in their order, the letter pages as School, the
  // rest folded under Under construction (newest first).
  const families = React.useMemo(() => pageFamilies(PAGES), [])
  const byRoot = new Map(families.map((family) => [family.root.id, family]))
  const ready = READY_SHELF.flatMap((id) => byRoot.get(id) ?? [])
  const school = SCHOOL_SHELF.flatMap((id) => byRoot.get(id) ?? [])
  const construction = families.filter((family) => shelfOf(family.root.id) === 'construction')
  const open = async (page: PageCard) => {
    if (busy) return
    setBusy(page.id)
    try {
      navigate({ page: 'local', id: await newPageDocument(page) })
    } finally {
      setBusy(null)
    }
  }
  const cards = (list: readonly PageFamily[], min: number) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 16 }}>
      {list.map((family) => (
        <PageCard key={family.root.id} family={family} dimmed={busy !== null && busy !== family.newest.id} onOpen={() => { void open(family.newest) }} />
      ))}
    </div>
  )
  return (
    <>
      {/* A step above the other shelves, not much more: the card art reads best small. */}
      <section aria-label="Pages">{cards(ready, 180)}</section>
      {school.length > 0 && <FoldedShelf title="School" storageKey="huebo:home:show-school" count={school.length}>{cards(school, 150)}</FoldedShelf>}
      {construction.length > 0 && <FoldedShelf title="Under construction" storageKey="huebo:home:show-construction" count={construction.length}>{cards(construction, 150)}</FoldedShelf>}
    </>
  )
}

const SHELF_HEADING: React.CSSProperties = { margin: 0, fontFamily: PAGE_NAME_FONT, fontWeight: 600, fontSize: 20, color: INK }

/** A shelf folded under its title (closed at first; open or closed is remembered). */
function FoldedShelf({ title, storageKey, count, children }: { title: string; storageKey: string; count: number; children: React.ReactNode }) {
  const [open, setOpen] = React.useState(() => {
    try { return localStorage.getItem(storageKey) === '1' } catch { return false }
  })
  const toggle = () => {
    setOpen(!open)
    try { localStorage.setItem(storageKey, open ? '0' : '1') } catch { /* storage unavailable */ }
  }
  return (
    <section aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        style={{ ...SHELF_HEADING, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', opacity: 0.7 }}
      >
        <span aria-hidden style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 160ms ease-out' }}>▸</span>
        {title} ({count})
      </button>
      {open && children}
    </section>
  )
}

function PlusGlyph({ color, size }: { color: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden style={{ pointerEvents: 'none' }}>
      <path d="M20 7 v26 M7 20 h26" stroke={INK} strokeOpacity={0.18} strokeWidth={9} strokeLinecap="round" transform="translate(0 1.5)" />
      <path d="M20 7 v26 M7 20 h26" stroke={color} strokeWidth={8} strokeLinecap="round" />
    </svg>
  )
}

/** "New" candidate: a fresh page (folded corner) with a sparkle in the theme's first colour. */
function NewDocGlyph({ color, size }: { color: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden style={{ pointerEvents: 'none', overflow: 'visible' }}>
      <path d="M9 6 h14 l8 8 v20 a2 2 0 0 1 -2 2 h-20 a2 2 0 0 1 -2 -2 v-26 a2 2 0 0 1 2 -2 Z" fill="#ffffff" stroke={INK} strokeWidth={3} strokeLinejoin="round" />
      <path d="M23 6 v6 a2 2 0 0 0 2 2 h6" fill="none" stroke={INK} strokeWidth={3} strokeLinejoin="round" />
      <path d="M31 17 q1.2 5.3 6.5 6.5 q-5.3 1.2 -6.5 6.5 q-1.2 -5.3 -6.5 -6.5 q5.3 -1.2 6.5 -6.5 Z" fill={color} stroke={INK} strokeWidth={1.6} strokeLinejoin="round" />
      <circle cx="17" cy="30" r="2" fill={color} />
    </svg>
  )
}

/**
 * A page in the grid: its newest version's picture with the family's name (its first page's)
 * underneath, growing a little under a mouse. Versions are the owner's business, in the page's
 * settings (src/play/pageVersions.ts).
 */
function PageCard({ family, dimmed, onOpen }: { family: PageFamily; dimmed: boolean; onOpen: () => void }) {
  const page = family.newest
  const Art = page.CardArt
  const grow = useHoverGrow()
  return (
    <button
      type="button"
      aria-label={family.root.title}
      title={family.root.title}
      onClick={onOpen}
      {...grow.handlers}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        border: 'none',
        padding: 0,
        background: 'transparent',
        cursor: 'pointer',
        opacity: dimmed ? 0.6 : 1,
        transform: growTransform(grow.hover),
        transition: HOVER_TRANSITION,
      }}
    >
      <span
        style={{
          position: 'relative',
          display: 'block',
          width: '100%',
          aspectRatio: '16 / 10',
          borderRadius: 20,
          overflow: 'hidden',
          background: page.accent,
          boxShadow: `0 6px 10px rgba(${INK_RGB}, 0.18)`,
        }}
      >
        {Art ? <span style={{ position: 'absolute', inset: 0 }}><Art /></span> : null}
      </span>
      <span aria-hidden style={{ alignSelf: 'center', fontFamily: PAGE_NAME_FONT, fontWeight: 600, fontSize: 17, lineHeight: 1.15, color: INK, textAlign: 'center' }}>
        {family.root.title}
      </span>
    </button>
  )
}
